import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { getClientEnv } from "@nosible/shared";
import { Button } from "@nosible/shared/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@nosible/shared/components/ui/card";
import { Input } from "@nosible/shared/components/ui/input";
import { Label } from "@nosible/shared/components/ui/label";
import { Separator } from "@nosible/shared/components/ui/separator";
import { useSession, authClient } from "@/account/lib/auth-client";
import { useTurnstile } from "@/account/hooks/use-turnstile";
import { sharedAuthRedirect } from "@/account/lib/auth-redirect";

const signUpSchema = z.object({
  name: z.string().min(2, "Name must be at least 2 characters"),
  email: z.string().email("Please enter a valid email address"),
});

type SignUpFormData = z.infer<typeof signUpSchema>;

export function SignUp() {
  const { data: session, isPending } = useSession();
  const [serverError, setServerError] = useState<string | null>(null);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState("");
  const [socialLoading, setSocialLoading] = useState<string | null>(null);
  const { turnstileRef, token: turnstileToken, isReady: turnstileReady, error: turnstileError, reset: resetTurnstile, enabled: turnstileEnabled } = useTurnstile({ action: "signup" });

  const captchaHeaders: Record<string, string> = turnstileToken ? { "x-captcha-response": turnstileToken } : {};

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<SignUpFormData>({
    resolver: zodResolver(signUpSchema),
  });

  const getRedirectUrl = () => {
    const env = getClientEnv();
    return sharedAuthRedirect(env?.APP_BASE_URL ?? "/", env);
  };

  useEffect(() => {
    if (session && !isPending && typeof window !== "undefined") {
      window.location.href = getRedirectUrl();
    }
  }, [session, isPending]);

  const handleSocialSignIn = async (provider: "github" | "google" | "microsoft" | "linkedin") => {
    setServerError(null);
    setSocialLoading(provider);
    const redirectUrl = getRedirectUrl();

    const { error } = await authClient.signIn.social({
      provider,
      callbackURL: redirectUrl,
    });

    if (error) {
      setServerError(error.message || `Failed to sign up with ${provider}`);
      setSocialLoading(null);
    }
  };

  const onSubmit = async (data: SignUpFormData) => {
    setServerError(null);
    const redirectUrl = getRedirectUrl();

    const { error } = await authClient.signIn.magicLink({
      email: data.email,
      name: data.name,
      callbackURL: redirectUrl,
      fetchOptions: { headers: captchaHeaders },
    });

    if (error) {
      setServerError(error.message || "Failed to send magic link");
      resetTurnstile();
    } else {
      setSubmittedEmail(data.email);
      setMagicLinkSent(true);
    }
  };

  if (isPending) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-obsidian">
        <div className="text-silica">Loading...</div>
      </div>
    );
  }

  if (magicLinkSent) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-obsidian">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Check your email</CardTitle>
            <CardDescription>
              We've sent a magic link to <strong>{submittedEmail}</strong>
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-chromium-mist mb-4">
              Click the link in the email to complete your sign up.
            </p>
            <Button
              onClick={() => setMagicLinkSent(false)}
              variant="outline"
              className="w-full"
            >
              Try a different email
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-obsidian">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Create an Account</CardTitle>
          <CardDescription>Sign up to access Nosible</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="space-y-3">
              <Button
                type="button"
                variant="outline"
                className="w-full"
                disabled={socialLoading !== null}
                onClick={() => handleSocialSignIn("github")}
              >
                <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
                </svg>
                {socialLoading === "github" ? "Connecting..." : "Continue with GitHub"}
              </Button>
              <Button
                type="button"
                variant="outline"
                className="w-full"
                disabled={socialLoading !== null}
                onClick={() => handleSocialSignIn("google")}
              >
                <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                </svg>
                {socialLoading === "google" ? "Connecting..." : "Continue with Google"}
              </Button>
              {/* <Button
                type="button"
                variant="outline"
                className="w-full"
                disabled={socialLoading !== null}
                onClick={() => handleSocialSignIn("microsoft")}
              >
                <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                  <path d="M11.4 24H0V12.6h11.4V24z" fill="#F25022" />
                  <path d="M24 24H12.6V12.6H24V24z" fill="#00A4EF" />
                  <path d="M11.4 11.4H0V0h11.4v11.4z" fill="#7FBA00" />
                  <path d="M24 11.4H12.6V0H24v11.4z" fill="#FFB900" />
                </svg>
                {socialLoading === "microsoft" ? "Connecting..." : "Continue with Microsoft"}
              </Button>
              <Button
                type="button"
                variant="outline"
                className="w-full"
                disabled={socialLoading !== null}
                onClick={() => handleSocialSignIn("linkedin")}
              >
                <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24" fill="#0A66C2">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                </svg>
                {socialLoading === "linkedin" ? "Connecting..." : "Continue with LinkedIn"}
              </Button> */}
            </div>

            <div className="flex items-center gap-3">
              <Separator className="flex-1" />
              <span className="text-xs text-chromium-mist">or</span>
              <Separator className="flex-1" />
            </div>

            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Name</Label>
                  <Input
                    id="name"
                    type="text"
                    placeholder="John Doe"
                    {...register("name")}
                  />
                  {errors.name && (
                    <p className="text-sm text-red-500">{errors.name.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    {...register("email")}
                  />
                  {errors.email && (
                    <p className="text-sm text-red-500">{errors.email.message}</p>
                  )}
                </div>

                {serverError && (
                  <p className="text-sm text-red-500">{serverError}</p>
                )}

                {turnstileEnabled && (
                  <div ref={turnstileRef} className="flex justify-center" />
                )}

                {turnstileError && (
                  <p className="text-sm text-yellow-500">{turnstileError}</p>
                )}

                <Button
                  type="submit"
                  className="w-full"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Sending magic link..." : "Sign Up with Magic Link"}
                </Button>

                <p className="text-center text-sm text-chromium-mist">
                  Already have an account?{" "}
                  <a href="/login" className="text-neon-green hover:underline">
                    Sign In
                  </a>
                </p>
              </div>
            </form>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
